import { Magnifier } from "@gravity-ui/icons";
import { Flex, Icon, Skeleton, Text, TextInput } from "@gravity-ui/uikit";
import { useMemo, useState } from "react";

import { CollectionVariableSection } from "./CollectionVariableSection";
import { ScalarVariableList } from "./ScalarVariableList";
import type { TemplateVariablesResponse } from "./TemplatesForm.types";
import css from "./VariableSidebar.module.scss";
import { buildScalarGroups } from "./VariableSidebar.utils";

interface VariableSidebarProps {
  data: TemplateVariablesResponse | null;
  isLoading: boolean;
  error: Error | null;
  onInsert: (placeholder: string) => void;
  // Сайдбар становится «приглушённым» когда активный редактор выключен —
  // кликать чипы бессмысленно, вставка пойдёт в никуда. Подсказка-плашка
  // показывает почему вставка недоступна.
  disabled?: boolean;
  disabledHint?: string;
}

export const VariableSidebar = ({
  data,
  isLoading,
  error,
  onInsert,
  disabled = false,
  disabledHint,
}: VariableSidebarProps) => {
  const [query, setQuery] = useState("");

  const scalarGroups = useMemo(
    () => buildScalarGroups(data, query),
    [data, query],
  );
  const collections = data?.collections ?? [];

  if (error) {
    return (
      <div className={css.sidebar}>
        <Text color="danger" variant="body-2">
          Не удалось загрузить переменные. Шаблоны можно вводить вручную.
        </Text>
      </div>
    );
  }

  return (
    <div
      className={`${css.sidebar} ${disabled ? css.sidebarDisabled : ""}`}
      data-disabled={disabled || undefined}
    >
      <div className={css.header}>
        <Text variant="subheader-2">Переменные</Text>
        <Text variant="caption-2" color="hint">
          Клик по чипу — вставка в курсор.
        </Text>
      </div>

      {disabled && disabledHint && (
        <div className={css.disabledHint} role="status">
          <Text variant="body-2">{disabledHint}</Text>
        </div>
      )}

      <TextInput
        size="m"
        placeholder="Поиск переменной"
        value={query}
        onUpdate={setQuery}
        hasClear
        disabled={disabled}
        startContent={
          <Flex alignItems="center" className={css.searchIcon}>
            <Icon data={Magnifier} size={14} />
          </Flex>
        }
      />

      <div className={css.scroll}>
        {isLoading && (
          <Flex direction="column" gap={2}>
            <Skeleton style={{ height: 80 }} />
            <Skeleton style={{ height: 120 }} />
          </Flex>
        )}

        {!isLoading && (
          <ScalarVariableList
            groups={scalarGroups}
            disabled={disabled}
            onPick={(variable) => onInsert(variable.placeholder)}
          />
        )}

        {!isLoading && scalarGroups.length === 0 && data && (
          <div className={css.empty}>Ничего не найдено</div>
        )}

        {!isLoading && (
          <CollectionVariableSection
            collections={collections}
            disabled={disabled}
            onInsert={onInsert}
          />
        )}
      </div>
    </div>
  );
};
